// Google Analytics and Search Console Integration

// Initialize Google Analytics
export const initGA = (measurementId) => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', measurementId, {
      page_title: document.title,
      page_location: window.location.href,
    });
  }
};

// Track page views
export const trackPageView = (url, title) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'GA_MEASUREMENT_ID', {
      page_path: url,
      page_title: title,
    });
  }
};

// Track events
export const trackEvent = (action, category, label, value) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track phone calls
export const trackPhoneCall = () => {
  trackEvent('phone_call', 'contact', 'header_call_button');
};

// Track form submissions
export const trackFormSubmission = (formType) => {
  trackEvent('form_submit', 'contact', formType);
};

// Track button clicks
export const trackButtonClick = (buttonName, location) => {
  trackEvent('button_click', 'engagement', `${buttonName}_${location}`);
};

// Track scroll depth
export const trackScrollDepth = (percentage) => {
  trackEvent('scroll', 'engagement', `${percentage}%`);
};

// Track Google Maps interactions
export const trackMapInteraction = (action) => {
  trackEvent('map_interaction', 'contact', action);
};

// SEO-specific tracking
export const trackSEOEvents = () => {
  // Track when users reach different sections
  const observerOptions = {
    threshold: 0.5,
    rootMargin: '0px 0px -100px 0px'
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const sectionName = entry.target.id || entry.target.className;
        trackEvent('section_view', 'engagement', sectionName);
      }
    });
  }, observerOptions);

  // Observe all main sections
  const sections = document.querySelectorAll('section[id]');
  sections.forEach(section => observer.observe(section));
};

// Track Core Web Vitals for Search Console
export const trackWebVitals = () => {
  if (typeof window !== 'undefined') {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS((metric) => {
        trackEvent('web_vitals', 'performance', 'CLS', Math.round(metric.value * 1000));
      });
      
      getFID((metric) => {
        trackEvent('web_vitals', 'performance', 'FID', Math.round(metric.value));
      });
      
      getFCP((metric) => {
        trackEvent('web_vitals', 'performance', 'FCP', Math.round(metric.value));
      });
      
      getLCP((metric) => {
        trackEvent('web_vitals', 'performance', 'LCP', Math.round(metric.value));
      });
      
      getTTFB((metric) => {
        trackEvent('web_vitals', 'performance', 'TTFB', Math.round(metric.value));
      });
    });
  }
};

// Search Console specific functions
export const reportToSearchConsole = {
  // Report successful page loads
  pageLoad: (url) => {
    if (typeof window !== 'undefined') {
      // This helps Search Console understand page performance
      trackEvent('page_load_success', 'technical_seo', url);
    }
  },

  // Report form interactions for better understanding of user engagement
  formInteraction: (formName) => {
    trackEvent('form_interaction', 'user_engagement', formName);
  },

  // Report search queries if implementing internal search
  internalSearch: (query, results) => {
    trackEvent('internal_search', 'site_search', query, results);
  },

  // Report 404 errors
  notFound: (url) => {
    trackEvent('404_error', 'technical_seo', url);
  }
};

// Local SEO tracking
export const trackLocalSEO = {
  // Track location-based interactions
  locationClick: (location) => {
    trackEvent('location_interaction', 'local_seo', location);
  },

  // Track service area interactions
  serviceAreaClick: (area) => {
    trackEvent('service_area_click', 'local_seo', area);
  },

  // Track Google Maps directions
  directionsClick: () => {
    trackEvent('directions_click', 'local_seo', 'google_maps');
    trackMapInteraction('get_directions');
  },

  // Track phone number clicks
  phoneClick: (source) => {
    trackEvent('phone_click', 'local_seo', source);
    trackPhoneCall();
  }
};

// Initialize all tracking when the app loads
export const initializeTracking = () => {
  // Initialize Google Analytics
  // initGA('YOUR_GA_MEASUREMENT_ID'); // Uncomment and add your GA ID
  
  // Track web vitals
  trackWebVitals();
  
  // Track SEO events
  trackSEOEvents();
  
  // Track scroll depth
  let scrollDepthTracked = [];
  window.addEventListener('scroll', () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );
    
    [25, 50, 75, 90].forEach(threshold => {
      if (scrollPercent >= threshold && !scrollDepthTracked.includes(threshold)) {
        trackScrollDepth(threshold);
        scrollDepthTracked.push(threshold);
      }
    });
  });
};
